<template>
  <v-data-table-virtual
    :headers="headers"
    :items="transferRequests"
    :loading="loadingTransferRequests"
    hover
    fixed-header
    style="min-height: 0"
    item-key="id"
    :header-props="{ class: 'text-left font-bold' }"
  >
    <template #loading>
      <v-skeleton-loader type="table-tbody@2"></v-skeleton-loader>
    </template>

    <template #item="{ item, props: rowProps, itemRef }">
      <tr
        v-bind="rowProps"
        :ref="itemRef"
        :class="{ 'selected-transfer': selectedTransferRequestId === item.id }"
        style="cursor: pointer"
        @click="openTransferRequestDrawer(item.id)"
      >
        <td>
          {{ item.beneficiaryName }}
          <UnreadCommentsMark :count="unreadCount(transferRequestCommentTargets(item))" />
        </td>
        <td>{{ item.beneficiaryIban }}</td>
        <td>{{ item.formattedAmount }}</td>
        <td>{{ item.formattedDate }}</td>
        <td>
          <v-chip
            v-tooltip="item.translatedStatus.tooltip"
            label
            size="small"
            append-icon="mdi-information-outline"
            :color="item.color"
          >
            {{ item.translatedStatus.title }}
          </v-chip>
        </td>
        <td class="flex items-center">
          <template v-if="item.files?.length">
            <v-icon size="small">mdi-paperclip</v-icon>
            <div class="ml-1">{{ item.files?.length }}</div>
          </template>
        </td>
        <td>
          <v-btn size="small" variant="text" icon="mdi-eye"></v-btn>
        </td>
      </tr>
    </template>

    <template #no-data>
      <div class="text-center py-3">
        {{ t("transfers.noTransfers") }}
      </div>
    </template>
  </v-data-table-virtual>
</template>

<script setup lang="ts">
import UnreadCommentsMark from "@/components/UnreadCommentsMark.vue";
import { useUnreadComments } from "@/composables/useComments";
import { useI18n } from "@/composables/useI18n";
import { useTransferRequests } from "@/composables/useTransferRequests";
import { useSelectedTransferRequestStore } from "@/store/selectedTransferRequest";
import { transferRequestCommentTargets } from "@/utils/comments";
import type { Timestamp } from "@firebase/firestore-types";
import { storeToRefs } from "pinia";
import { toRef } from "vue";

const props = defineProps<{
  accountId: string;
  beneficiaryIban: string;
}>();

const { t } = useI18n();
const accountId = toRef(props, "accountId");
const beneficiaryIban = toRef(props, "beneficiaryIban");

const { selectedTransferRequestId } = storeToRefs(useSelectedTransferRequestStore());
const { openTransferRequestDrawer } = useSelectedTransferRequestStore();

const { transferRequests, loadingTransferRequests } = useTransferRequests(accountId, {
  beneficiaryIban,
  status: toRef(() => "pending"),
});
const { unreadCount } = useUnreadComments(accountId);

const headers = [
  { key: "beneficiaryName", title: t("transfers.beneficiaryName"), sortable: true },
  { key: "beneficiaryIban", title: t("transfers.beneficiaryIban"), sortable: true },
  {
    key: "amount",
    title: t("common.amount"),
    sortable: true,
    sort: (a: string, b: string) => parseFloat(a) - parseFloat(b),
  },
  {
    key: "createdAt",
    title: t("common.date"),
    sortable: true,
    sort: (a: Timestamp, b: Timestamp) => a.toMillis() - b.toMillis(),
  },
  { key: "status", title: t("common.status"), sortable: true },
  { key: "files", title: t("common.files"), sortable: true },
  { key: "actions", title: t("common.actions"), sortable: false },
];
</script>

<style scoped>
.selected-transfer {
  background-color: rgba(var(--v-theme-on-surface), 0.08) !important;
}
</style>
